import React, { useState, useEffect } from 'react';
import { AIStarIcon, CheckIcon, ChevronDownIcon, HardhatIcon, DataIcon, ToolIcon, ShieldIcon } from '../components/Icons';
import { ContactSection } from '../components/Layout';

// Construction-specific icons
const BlueprintIcon = () => (
  <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="3" y="3" width="18" height="18" rx="2" stroke="currentColor" strokeWidth="2"/>
    <path d="M3 9H21" stroke="currentColor" strokeWidth="2"/>
    <path d="M9 9V21" stroke="currentColor" strokeWidth="2"/>
    <path d="M13 13H17" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
    <path d="M13 17H17" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
  </svg>
);

const ReportIcon = () => (
  <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M14 2H6C5.45 2 4.95 2.22 4.59 2.59C4.22 2.95 4 3.45 4 4V20C4 20.55 4.22 21.05 4.59 21.41C4.95 21.78 5.45 22 6 22H18C18.55 22 19.05 21.78 19.41 21.41C19.78 21.05 20 20.55 20 20V8L14 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M14 2V8H20" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M8 13H16" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
    <path d="M8 17H16" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
  </svg>
);

function Construction() {
  const [scrollY, setScrollY] = useState(0);

  useEffect(() => {
    window.scrollTo(0, 0);
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (sectionId) => {
    document.getElementById(sectionId)?.scrollIntoView({ behavior: 'smooth' });
  };

  const services = [
    {
      icon: <HardhatIcon />,
      title: 'AI-Powered Estimating',
      description: 'Paste an email, get a budget estimate. Your AI agent reads scope descriptions, identifies cost drivers, applies your rates, and builds line-item estimates — in minutes, not days.'
    },
    {
      icon: <DataIcon />,
      title: 'Project Data Architecture',
      description: 'Transform estimating spreadsheets into structured databases. Track projects, areas, assemblies, and line items with proper hierarchy. Query anything in plain English.'
    },
    {
      icon: <BlueprintIcon />,
      title: 'Complexity Analysis',
      description: 'Data-driven complexity scoring based on actual project metrics — labor hours per cubic yard, steel density, embed counts. Know what you\'re walking into before you bid.'
    },
    {
      icon: <ReportIcon />,
      title: 'Automated Reporting',
      description: 'Project status, cost tracking, labor analysis — generated automatically from your data. No more end-of-month scrambles. Your agent keeps the numbers current.'
    },
    {
      icon: <ToolIcon />,
      title: 'Custom Estimating Tools',
      description: 'Your agent builds the tools you need. Shoring calculators, concrete placement estimators, crew loading spreadsheets — purpose-built for your specific work.'
    },
    {
      icon: <ShieldIcon />,
      title: 'Private & Secure',
      description: 'Your project data stays on your infrastructure. No cloud estimating platforms with access to your pricing. No shared databases. You own your competitive advantage.'
    },
  ];

  const proofMetrics = [
    {
      value: '25+ Years',
      label: 'In the field',
      description: 'Tradesman to foreman to estimator to project manager. We know what a realistic crew loading looks like, what gets missed in takeoffs, and why projects bleed money.'
    },
    {
      value: 'Real Data',
      label: 'Not theoretical AI',
      description: 'Our tools are built from actual project experience — real labor hours, real costs, real complexity. Not benchmarks from a textbook. Proven on heavy industrial work.'
    },
    {
      value: 'Minutes',
      label: 'Email to estimate',
      description: 'Paste the scope email. Your AI agent extracts the work, identifies cost drivers, applies your rates and markups, and returns a structured estimate. What took days now takes minutes.'
    }
  ];

  const processSteps = [
    {
      step: '01',
      title: 'Data Migration',
      description: 'We structure your historical project data — past estimates, actual costs, labor hours — into a proper database your AI can learn from. Your experience becomes searchable intelligence.'
    },
    {
      step: '02',
      title: 'Agent Training',
      description: 'Your agent learns your rates, your markups, your typical crew compositions, your regional factors. It doesn\'t guess — it estimates like you would, because it learned from your data.'
    },
    {
      step: '03',
      title: 'Workflow Integration',
      description: 'Email to estimate. Drawing to takeoff. Change order to budget. Your agent plugs into your existing workflow — you keep working the way you work, just faster and with fewer mistakes.'
    }
  ];

  const heroBenefits = [
    'Estimate from email scope in minutes, not days',
    'Project data architecture built for construction',
    'Complexity metrics from real project data'
  ];

  return (
    <>
      <section className="hero">
        <div className="hero-background">
          <div className="hero-grid"></div>
          <div className="hero-background-star" style={{ transform: `translate(-50%, -50%) translateY(${scrollY * 0.3}px)` }}>
            <AIStarIcon />
          </div>
          <div className="hero-gradient"></div>
        </div>
        <div className="container">
          <div className="hero-inner">
            <div className="hero-copy">
              <p className="hero-eyebrow">Built by a construction veteran</p>
              <h1 className="hero-title">
                AI Construction Synthesis
              </h1>
              <p className="hero-subtitle">
                Purpose-built AI for heavy industrial construction. Estimating, project data, complexity analysis — built by someone who's actually run the crews and bid the work.
              </p>
              <div className="hero-buttons">
                <button
                  className="btn btn-primary"
                  onClick={() => scrollToSection('contact')}
                >
                  Get Started
                </button>
                <button
                  className="btn btn-secondary"
                  onClick={() => scrollToSection('capabilities')}
                >
                  See Capabilities
                </button>
              </div>
            </div>
            <div className="hero-side">
              <div className="hero-side-card">
                <h3>What changes on day one</h3>
                <ul className="hero-benefits">
                  {heroBenefits.map((benefit, index) => (
                    <li key={index} className="hero-benefit">
                      <span className="hero-benefit-icon"><CheckIcon /></span>
                      <span>{benefit}</span>
                    </li>
                  ))}
                </ul>
                <div className="hero-side-footer">Heavy industrial • Foundations • Infrastructure</div>
              </div>
            </div>
          </div>
        </div>
        <div className="scroll-indicator" onClick={() => scrollToSection('proof')}>
          <ChevronDownIcon />
        </div>
      </section>

      {/* Proof / Metrics */}
      <section id="proof" className="results">
        <div className="container">
          <div className="section-header">
            <h2>Built on real data</h2>
            <p>Not theoretical AI. Built from actual project data, actual labor hours, actual costs.</p>
          </div>
          <div className="results-grid">
            {proofMetrics.map((metric, index) => (
              <div key={index} className="result-card">
                <span className="result-value">{metric.value}</span>
                <span className="result-label">{metric.label}</span>
                <p>{metric.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Capabilities */}
      <section id="capabilities" className="services">
        <div className="container">
          <div className="section-header">
            <h2>What your AI does for construction</h2>
            <p>Every tool built specifically for how construction estimating and project management actually works.</p>
          </div>
          <div className="services-grid">
            {services.map((service, index) => (
              <div key={index} className="service-card">
                <div className="service-icon">
                  {service.icon}
                </div>
                <h3>{service.title}</h3>
                <p>{service.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* About */}
      <section className="about">
        <div className="container">
          <div className="about-content">
            <div className="about-text">
              <h2>I've been on every side of the table.</h2>
              <p>Tradesman. Foreman. Estimator. Project manager. 25+ years in heavy industrial construction. I know what a realistic crew loading looks like. I know what gets missed in takeoffs. I know why projects bleed money.</p>
              <p>Most construction software is built by developers who've never been on a jobsite. They build what looks good in a demo. We build what actually works when you're staring at a 200-page set of drawings and a two-week bid deadline.</p>
              <p>The AI doesn't replace your experience — it amplifies it. It learns your rates, your markups, your typical crew compositions. It estimates like you would, just faster.</p>
              <p><strong>Your decades of knowledge + AI speed = unfair advantage.</strong></p>
              <p className="founder-sig">- Keaton Valentine, Founder</p>
            </div>
            <div className="about-visual">
              <div className="about-highlight">
                <h3>Domain expertise matters</h3>
                <div className="about-differentiators">
                  <div className="differentiator">
                    <h4>Not generic AI.</h4>
                    <p>Your agent knows the difference between a mat foundation and a spread footing. It understands anchor bolt patterns, embed schedules, and why deep excavation shoring costs exponentially more per linear foot.</p>
                  </div>
                  <div className="differentiator">
                    <h4>Built from real experience.</h4>
                    <p>Our tools are informed by decades of hands-on heavy industrial construction — real labor hours, real complexity, real cost drivers. Not benchmarks from a textbook.</p>
                  </div>
                  <div className="differentiator">
                    <h4>Your data, your advantage.</h4>
                    <p>Every estimate you run makes your AI smarter. Your historical data becomes searchable intelligence. Your pricing stays on your servers — not in some cloud platform your competitor also uses.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="process">
        <div className="container">
          <div className="section-header">
            <h2>From spreadsheets to AI in weeks</h2>
            <p>We migrate your data, train your agent on your work, and integrate into your workflow.</p>
          </div>
          <div className="process-steps">
            {processSteps.map((step) => (
              <div key={step.step} className="process-step">
                <span className="process-number">{step.step}</span>
                <h3>{step.title}</h3>
                <p>{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonial */}
      <section className="testimonial">
        <div className="container">
          <div className="testimonial-card">
            <p className="testimonial-quote">
              "My agent estimated a construction job from two pasted emails this morning. Then it designed a print banner for my company. Then it reviewed my landing page source code. Before lunch. It's not a tool I use — it's a coworker that never clocks out."
            </p>
            <div className="testimonial-author">
              <span className="author-name">Keaton Valentine</span>
              <span className="author-company">whatifWE LLC — Running his own agent since 2025</span>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="cta-banner">
        <div className="container">
          <div className="cta-content">
            <h2>Ready to estimate smarter?</h2>
            <p>Book a call and we'll map out how AI fits into your estimating workflow. Bring your ugliest spreadsheet — we've seen worse.</p>
            <ul className="cta-points">
              <li><span className="hero-benefit-icon"><CheckIcon /></span><span>AI estimating from email scope in minutes</span></li>
              <li><span className="hero-benefit-icon"><CheckIcon /></span><span>Your data stays on your infrastructure</span></li>
              <li><span className="hero-benefit-icon"><CheckIcon /></span><span>Built by someone who's actually done the work</span></li>
            </ul>
            <button className="btn btn-primary" onClick={() => scrollToSection('contact')}>
              Let's Talk
            </button>
          </div>
        </div>
      </section>

      <ContactSection />
    </>
  );
}

export default Construction;
